import { isFM, isScm } from '../../utils/role';

export const projects = {
    tabName: 'Projects',
    tabID: 'projects',
    path: 'projects',
};

export const tickets = {
    tabName: 'Tickets',
    tabID: 'tickets',
    path: 'tickets',
};

export const invoices = {
    tabName: 'Invoices',
    tabID: 'invoices',
    path: 'invoices',
};

export const getCustomerBillingTabs = () => {
    return [tickets, invoices];
};

const getTabFromQueryString = (tabs, urlQueryString) => {
    const match = urlQueryString.match(/[\?&]tab=(\w+)/);
    let queryTab;
    if (match) queryTab = match[1];
    const matchingTab = tabs.find((tab) => tab.path === queryTab);
    if (matchingTab) return { matchingTab };
    return {};
};

export const getCustomerBillingsDefaultTab = ({ urlQueryString }) => {
    const tabs = getCustomerBillingTabs();
    let tab = tabs[0];
    if (urlQueryString) {
        const { matchingTab } = getTabFromQueryString(tabs, urlQueryString);
        if (matchingTab) {
            tab = matchingTab;
        }
    }
    return tab?.path;
};

export const defaultRowsPerPage = 10;
export const defaultPage = 1;

// Views inside the Tickets tab: the existing ticket list, or the approval remarks grouped by marking batch.
export const ticketView = {
    tabName: 'Tickets',
    tabID: 'ticket_list',
    path: 'ticket_list',
};

export const remarkView = {
    tabName: 'Billing Remarks',
    tabID: 'remarks',
    path: 'remarks',
};

export const getCustomerBillingViews = () => [remarkView, ticketView];

export const getCustomerBillingDefaultView = ({ urlQueryString } = {}) => {
    const views = getCustomerBillingViews();
    const match = urlQueryString?.match(/[?&]view=(\w+)/);
    const matchingView = views.find((view) => view.path === match?.[1]);
    return matchingView ? matchingView.path : views[0].path;
};

export const BillingApprovalReason = {
    AUTO_APPROVAL: 'AUTO_APPROVAL',
    UNDER_AMC: 'UNDER_AMC',
    APPROVAL_ATTACHMENT: 'APPROVAL_ATTACHMENT',
};

export const BillingStatus = {
    SUBMITTED: 'SUBMITTED',
    REJECTED: 'REJECTED',
    RECEIVED: 'RECEIVED',
    REVOKED: 'REVOKED',
    INVOICED: 'INVOICED',
};

export const getTicketBillingStatus = (ticket) => {
    if (ticket?.invoice_id || ticket?.billing_status === BillingStatus.INVOICED) return BillingStatus.INVOICED;
    if (ticket?.billing_status) return ticket.billing_status;
    return ticket?.customer_approval_received === true ? BillingStatus.RECEIVED : null;
};

export const isCustomerBillingPending = (ticket) => {
    const status = getTicketBillingStatus(ticket);
    return status === null || status === BillingStatus.REVOKED;
};

// Reasons are not served by an API, so the options live here and are reused by the modal, the filters and the list.
export const billingApprovalReasonOptions = [
    { key: BillingApprovalReason.AUTO_APPROVAL, value: BillingApprovalReason.AUTO_APPROVAL, label: 'Auto Approval' },
    { key: BillingApprovalReason.UNDER_AMC, value: BillingApprovalReason.UNDER_AMC, label: 'Under AMC' },
    { key: BillingApprovalReason.APPROVAL_ATTACHMENT, value: BillingApprovalReason.APPROVAL_ATTACHMENT, label: 'Approval Attachment' },
];

export const getBillingApprovalReasonLabel = (reason) => {
    if (reason === BillingStatus.SUBMITTED) return 'Customer Billing Submitted';
    if (reason === BillingStatus.REJECTED) return 'Customer Billing Rejected';
    if (reason === BillingStatus.RECEIVED) return 'Customer Billing Received';
    return billingApprovalReasonOptions.find(({ value }) => value === reason)?.label || reason || '-';
};

// The remark list is grouped by reason, so each one gets its own badge colour to scan by.
const billingApprovalReasonStyles = {
    [BillingStatus.SUBMITTED]: 'bg-purple-50 text-purple-700 border border-purple-200',
    [BillingStatus.REJECTED]: 'bg-red-50 text-red-700 border border-red-200',
    [BillingStatus.RECEIVED]: 'bg-green-50 text-green-700 border border-green-200',
    [BillingApprovalReason.AUTO_APPROVAL]: 'bg-blue-50 text-blue-700 border border-blue-200',
    [BillingApprovalReason.UNDER_AMC]: 'bg-indigo-50 text-indigo-700 border border-indigo-200',
    [BillingApprovalReason.APPROVAL_ATTACHMENT]: 'bg-purple-50 text-purple-700 border border-purple-200',
};

export const getBillingApprovalReasonStyle = (reason) => billingApprovalReasonStyles[reason] || 'bg-gray-50 text-scogogray border border-scogoddd';

// A marking batch stays pending until one of its tickets is invoiced, and is invoiced once all of them are.
export const getRemarkInvoiceStatus = ({ ticket_count = 0, invoiced_ticket_count = 0 } = {}) => {
    if (ticket_count > 0 && invoiced_ticket_count >= ticket_count) {
        return { label: 'Invoiced', className: 'bg-green-50 text-green-700 border border-green-200' };
    }
    if (invoiced_ticket_count > 0) {
        return { label: 'Partially Invoiced', className: 'bg-yellow-50 text-yellow-700 border border-yellow-200' };
    }
    return { label: 'Invoice Pending', className: 'bg-orange-50 text-scogoorange border border-orange-200' };
};

// The mark and revoke endpoints accept at most 500 ticket ids per request.
export const maxTicketsPerBillingApproval = 500;

